// @ts-nocheck
import { connect } from 'react-redux';
import { createToolbarEvent } from 'jitsi/analytics/AnalyticsEvents';
import { sendAnalytics } from 'jitsi/analytics/functions';
import { translate } from 'jitsi/base/i18n/functions';
import { IconSubtitles } from 'jitsi/base/icons/svg';
import { MEET_FEATURES } from 'jitsi/base/jwt/constants';
import { maybeShowPremiumFeatureDialog } from 'jitsi/jaas/actions.web';
import { toggleLanguageSelectorDialog } from 'jitsi/subtitles/actions.web';
import { AbstractClosedCaptionButton } from 'jitsi/subtitles/components/AbstractClosedCaptionButton';
import { isLocalParticipantModerator } from 'jitsi/base/participants/functions';
/**
 * A button which starts/stops the transcriptions.
 * 11sight extension: Matches the original 11sight behavior - simple label and functionality.
 * Overrides the dynamic label/tooltip behavior with static properties like original 11sight.
 * Extends AbstractClosedCaptionButton directly (same as base ClosedCaptionButton does).
 */
class IISightClosedCaptionButton extends AbstractClosedCaptionButton {
    accessibilityLabel = 'toolbar.accessibilityLabel.cc';
    icon = IconSubtitles;
    tooltip = 'transcribing.ccButtonTooltip';
    label = 'toolbar.startSubtitles';
    labelProps = {
        language: this.props.t(this.props._language),
        languages: this.props.t(this.props.languages ?? ''),
        languagesHead: this.props.t(this.props.languagesHead ?? '')
    };
    /**
     * Handles clicking / pressing the button.
     * 11sight extension: Matches original 11sight behavior - async method with await for premium dialog.
     * The original 11sight had async _handleClick with await, which ensures proper dialog handling.
     * This matches the original 11sight AbstractClosedCaptionButton exactly.
     *
     * @override
     * @protected
     * @returns {Promise<void>}
     */
    async _handleClick() {
        const { _requestingSubtitles, dispatch } = this.props;
        sendAnalytics(createToolbarEvent('transcribing.ccButton', {
            'requesting_subtitles': Boolean(_requestingSubtitles)
        }));
        // 11sight: Use await to match original behavior - wait for premium dialog to complete
        const dialogShown = await dispatch(maybeShowPremiumFeatureDialog(MEET_FEATURES.RECORDING));
        if (!dialogShown) {
            this._handleClickOpenLanguageSelector();
        }
    }
    /**
     * Override dynamic label getter to always return the static label.
     * This matches the original 11sight behavior where label was static.
     *
     * @returns {string} The label key.
     */
    _getLabel() {
        return 'toolbar.startSubtitles';
    }
    /**
     * Override dynamic accessibility label getter to always return the static label.
     * This matches the original 11sight behavior.
     *
     * @returns {string} The accessibility label key.
     */
    _getAccessibilityLabel() {
        return 'toolbar.accessibilityLabel.cc';
    }
    /**
     * Override dynamic tooltip getter to always return the static tooltip.
     * This matches the original 11sight behavior.
     *
     * @returns {string} The tooltip key.
     */
    _getTooltip() {
        return 'transcribing.ccButtonTooltip';
    }
    /**
     * Toggle language selection dialog.
     * 11sight extension: Matches original behavior - always opens language selector dialog.
     * Original 11sight didn't have CC tab logic, so we always use the language selector.
     *
     * @returns {void}
     */
    _handleClickOpenLanguageSelector() {
        const { dispatch } = this.props;
        dispatch(toggleLanguageSelectorDialog());
    }
}
/**
 * Maps redux state to component props.
 * 11sight extension: Matches the original 11sight mapStateToProps exactly.
 * Original 11sight used _abstractMapStateToProps directly, which already handled visibility.
 * The original _abstractMapStateToProps returned { _requestingSubtitles, _language, visible } (no _isCCTabEnabled).
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The component's own props.
 * @returns {Object} Mapped props for the component.
 */
function mapStateToProps(state, ownProps) {
    // Original 11sight visibility logic: transcription?.enabled && (isLocalParticipantModerator || isTranscribing)
    // Match original 11sight _abstractMapStateToProps exactly - use same visibility logic
    const { transcription } = state['features/base/config'];
    const { _requestingSubtitles, _language } = state['features/subtitles'];
    const { isTranscribing: isTranscribingValue } = state['features/transcribing'];
    // Original 11sight visibility logic from _abstractMapStateToProps
    const { visible = Boolean(transcription?.enabled
        && (isLocalParticipantModerator(state) || isTranscribingValue)) } = ownProps;
    // Match original 11sight _abstractMapStateToProps return value exactly
    // Original returned: { _requestingSubtitles, _language, visible }
    // No _isCCTabEnabled property in original 11sight
    return {
        _requestingSubtitles,
        _language,
        visible
    };
}
export default translate(connect(mapStateToProps)(IISightClosedCaptionButton));
